import {objectFromEntries} from '@benchling/js-utils';
import moment from 'moment';

import {assertAndContinue} from 'Asserts';
import {NameTemplatePartNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getOptionsForNameTemplatePartType} from 'name-templates/GetOptionsForNameTemplatePartType';
import {
  NameTemplatePart,
  NameTemplatePartType,
  NameTemplateType,
  SUPPORTED_NAME_TEMPLATE_PART_TYPES_BY_NAME_TEMPLATE_TYPE,
} from 'name-templates/NameTemplateConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';

import NameTemplatePartConfiguration from './NameTemplatePartConfiguration';

export function makeExampleForNameTemplate(
  nameTemplateParts: Array<NameTemplatePart>,
  schemaFields: Array<SchemaField | ClientSchemaField>,
  nameTemplatePartConfiguration?: NameTemplatePartConfiguration
): string {
  return nameTemplateParts
    .map((part) => _makeExampleForPart(part, schemaFields, nameTemplatePartConfiguration))
    .join('');
}

export function _makeExampleForPart(
  part: NameTemplatePart,
  schemaFields: Array<SchemaField | ClientSchemaField>,
  nameTemplatePartConfiguration?: NameTemplatePartConfiguration
): string {
  // Uses override example value if one is generated by the configuration. Else defaults to default example value.
  const exampleValue = nameTemplatePartConfiguration?.getExamplePartValue?.(part.type);
  if (exampleValue != null) {
    return exampleValue;
  }

  switch (part.type) {
    case NameTemplatePartType.ANTIBODY_DOMAIN:
      return `entity_${part.domain_type}${part.specificity}`;
    case NameTemplatePartType.CREATED_AT_YEAR:
    case NameTemplatePartType.CONTAINER_CREATED_AT_YEAR:
    case NameTemplatePartType.FILL_YEAR:
      return String(new Date().getFullYear());
    case NameTemplatePartType.CREATED_AT_DATE:
    case NameTemplatePartType.CONTAINER_CREATED_AT_DATE:
      return moment(new Date()).format('YYYYMMDD').toString();
    case NameTemplatePartType.FILL_DATE:
      return moment(new Date()).format('YYYY-MM-DD').toString();
    case NameTemplatePartType.FIELD:
    case NameTemplatePartType.COMPLEX_POLYMER_COMPONENT: {
      const field = schemaFields.find((f) => f.apiId === part.fieldId);
      if (field) {
        return makeExampleForField(field);
      } else {
        assertAndContinue(false, 'Expected to find field ID in schemaFields array.');
        return '?';
      }
    }
    case NameTemplatePartType.CHILD_ENTITY_LOT_NUMBER:
    case NameTemplatePartType.SAMPLE_LOT_NUMBER:
      return '003';
    case NameTemplatePartType.LINKED_BIOENTITY_REGISTRY_IDENTIFIER:
      return 'p012';
    case NameTemplatePartType.HOISTED_FIELD:
      return 'HoistedValue';
    case NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER:
      return '012';
    case NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER_BASE_26:
      return 'AAO';
    case NameTemplatePartType.BARCODE:
      return 'tube456';
    case NameTemplatePartType.BARCODE_NUMBER:
      return '456';
    case NameTemplatePartType.QUANTITY_UNITS:
      return 'mL';
    case NameTemplatePartType.CONCENTRATION_UNITS:
      return 'g/mL';
    case NameTemplatePartType.CONCENTRATION_VALUE:
    case NameTemplatePartType.QUANTITY_VALUE:
      return '5';
    case NameTemplatePartType.CONTAINED_ENTITY_NAME:
      return 'entityname';
    case NameTemplatePartType.PARENT_STORABLE_BARCODE:
      return 'box789';
    case NameTemplatePartType.PARENT_STORABLE_NAME:
      return 'boxname';
    case NameTemplatePartType.POSITION:
      return 'A1';
    case NameTemplatePartType.PROJECT:
      return 'project';
    case NameTemplatePartType.RESTRICTION_STATUS:
      return 'Restricted';
    case NameTemplatePartType.TEXT:
    case NameTemplatePartType.SEPARATOR:
      return part.text;
    case NameTemplatePartType.STUDY_NAME:
      return 'StudyName';
    case NameTemplatePartType.STUDY_ID:
      assertAndContinue(
        false,
        `Expected for ${part.type} to be supported by getExamplePartValue function provided in nameTemplatePartConfiguration
        as it depends on the study schema prefix to generate an example value.`
      );
      return 'StudyID';
    case NameTemplatePartType.PROCEDURE_NAME:
      assertAndContinue(
        false,
        `Expected for ${part.type} to be supported by getExamplePartValue function provided in nameTemplatePartConfiguration
        as it depends on the study type to generate an example value.`
      );
      return 'ProcedureName';
    case NameTemplatePartType.PROCEDURE_RUN_ID:
      assertAndContinue(
        false,
        `Expected for ${part.type} to be supported by getExamplePartValue function provided in nameTemplatePartConfiguration
        as it depends on the study type to generate an example value.`
      );
      return 'ProcedureRunID';
    default:
      // @ts-expect-error
      assertAndContinue(false, `Unexpected part type: ${part.type}`);
      return '?';
  }
}

export function makeExampleForField(field: SchemaField | ClientSchemaField): string {
  switch (field.fieldType) {
    case FieldType.INTEGER:
      return String(73);
    case FieldType.FLOAT:
      return String(12.1);
    case FieldType.SELECTOR: {
      if (field.schemaFieldSelectorId) {
        const selector = SchemaFieldSelectorStore.getSelectorById(field.schemaFieldSelectorId);
        if (selector && selector.schemaFieldSelectorOptions.length > 0) {
          return selector.schemaFieldSelectorOptions[0].name;
        }
      }
      break;
    }
    default:
      break;
  }
  return `sample${field.name.replace(/\s/g, '').toLowerCase()}`;
}

export function graphQLNameTemplatePartsToProps(
  graphQLNameTemplateParts: Array<NameTemplatePartNode>
): Array<NameTemplatePart> {
  return graphQLNameTemplateParts.map(
    (graphQLNameTemplatePart) =>
      objectFromEntries(
        Object.entries(graphQLNameTemplatePart).filter(([k, v]) => v != null && k !== '__typename')
      ) as NameTemplatePart
  );
}

export function getSupportedNameTemplatePartOptions(
  nameTemplateType: NameTemplateType,
  schemaFields: Array<SchemaField | ClientSchemaField>,
  appendPartFunc: (part: NameTemplatePart) => void,
  complexPolymerFields?: Array<SchemaField | ClientSchemaField>,
  nameTemplatePartConfiguration?: NameTemplatePartConfiguration
): Array<OptionData> {
  const supportedNamePartSections =
    SUPPORTED_NAME_TEMPLATE_PART_TYPES_BY_NAME_TEMPLATE_TYPE[nameTemplateType];
  const options: Array<OptionData> = [];
  for (const namePartSection of supportedNamePartSections) {
    if (namePartSection.sectionHeader != null) {
      options.push({
        name: namePartSection.sectionHeader,
        isHeader: true,
        className: 'schema-section',
      });
    }
    namePartSection.namePartTypes.forEach((supportedNamePart) => {
      options.push(
        ...getOptionsForNameTemplatePartType(
          supportedNamePart,
          schemaFields,
          appendPartFunc,
          complexPolymerFields,
          nameTemplatePartConfiguration
        )
      );
    });
  }
  return options;
}
